import React, { useState } from "react";

import {
  FlexComp,
  FormattedText,
  ImageComponent,
  ParaTag,
  SpanTag,
} from "@tm/static";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";

import {
  Card,
  CardCtaButtonList,
  CardCtaDivider,
  CardPadding,
  InfoStrip,
  ListBox,
  ListRow,
  RowMeta,
  SectionHeaderIcon,
} from "../../OrderDetailV2.style";
import { useOrderDetailV2 } from "../../OrderDetailV2Context";
import type { Nominee, OrderDetailV2NomineeProps } from "../../types";
import { AddNomineeModal } from "../AddNomineeModal";
import { OrderDetailV2Tooltip } from "../OrderDetailV2Tooltip";
import { TextButton } from "../TextButton";

export const OrderDetailV2Nominee: React.FC<OrderDetailV2NomineeProps> = ({
  icon,
  title,
  actionLabel,
  isActionDisabled,
  actionDisabledReason,
  isActionVisible = true,
  persuasionText,
  nominees = [],
  formConfig,
  modalConfig,
  entities = [],
  actionList = [],
}: OrderDetailV2NomineeProps) => {
  const ctx = useOrderDetailV2();
  const disabled = Boolean(isActionDisabled);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const device = useBreakpointV2();

  const openNomineeModal = () => {
    if (!disabled && formConfig) {
      setIsModalOpen(true);
    }
  };

  return (
    <Card>
      <CardPadding>
        <FlexComp
          alignItems="center"
          justifyContent="space-between"
          margin="0 0 10px"
        >
          <FlexComp alignItems="center" gap="8px" margin="0">
            {device === "lg" && (
              <SectionHeaderIcon>
                <ImageComponent src={icon} width="20px" height="20px" />
              </SectionHeaderIcon>
            )}
            <SpanTag
              fontSize="large"
              fontWeight="bold"
              color={device === "lg" ? theme.greyText : theme.blackText}
            >
              <FormattedText value={title || "Nominee Details (Optional)"} />
            </SpanTag>
          </FlexComp>
          {device === "lg" && isActionVisible && actionLabel && (
            <OrderDetailV2Tooltip
              message={actionDisabledReason || ""}
              trigger="both"
              show={disabled}
              position="bottom"
              autoHideDelay={5000}
              disabled={!disabled || !actionDisabledReason}
            >
              <TextButton
                btnTxt={actionLabel}
                onClick={openNomineeModal}
                disabled={disabled}
                showDefaultRightIcon={false}
              />
            </OrderDetailV2Tooltip>
          )}
        </FlexComp>

        {persuasionText && device === "lg" ? (
          <InfoStrip>
            <SpanTag fontSize="xs" color={theme.greyText7}>
              <FormattedText value={persuasionText} />
            </SpanTag>
          </InfoStrip>
        ) : null}

        {nominees.length > 0 && (
          <ListBox>
            {nominees.map((nominee: Nominee, index: number) => {
              return (
                <ListRow key={`${nominee?.entityId || "nominee"}-${index}`}>
                  <FlexComp
                    alignItems="flex-start"
                    justifyContent="space-between"
                    margin="0"
                  >
                    <div style={{ minWidth: 0 }}>
                      <ParaTag fontSize="md" fontWeight="bold">
                        <FormattedText value={nominee?.title || "Nominee"} />
                        {nominee?.subTitle && (
                          <>
                            {" - "}
                            <SpanTag fontWeight="regular" fontSize="md">
                              <FormattedText value={nominee.subTitle} />
                            </SpanTag>
                          </>
                        )}
                      </ParaTag>
                      {nominee?.desc ? (
                        <RowMeta>
                          <SpanTag fontSize="xs" color={theme.greyText}>
                            <FormattedText value={nominee.desc} />
                          </SpanTag>
                        </RowMeta>
                      ) : null}
                    </div>
                  </FlexComp>
                </ListRow>
              );
            })}
          </ListBox>
        )}
      </CardPadding>

      {device === "sm" && (
        <>
          <CardCtaDivider />
          <CardCtaButtonList>
            {actionList.length > 0
              ? actionList.map((action: any) => (
                  <TextButton
                    key={action?.id}
                    fullWidth={true}
                    btnTxt={action?.label || ""}
                    onClick={openNomineeModal}
                    disabled={disabled}
                  />
                ))
              : actionLabel && (
                  <TextButton
                    fullWidth={true}
                    btnTxt={actionLabel}
                    onClick={openNomineeModal}
                    disabled={disabled}
                  />
                )}
          </CardCtaButtonList>
        </>
      )}

      {formConfig && ctx?.bookingId && (
        <AddNomineeModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          formConfig={formConfig}
          entities={entities}
          bookingId={ctx.bookingId}
          onSave={() => {}}
          onEndorsementSuccess={ctx.refreshOrderDetails}
          modalTitle={modalConfig?.header?.title || "Add Nominees"}
          saveButtonLabel={
            modalConfig?.footer?.cta?.[0]?.label || "SAVE DETAILS"
          }
        />
      )}
    </Card>
  );
};
